import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, FileDown, Loader2, ReceiptText, Pencil, Eye } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { useDebounced, useLatestRequest } from '../../../lib/searchHooks';
import { fmtINR, formatErr, notifyErr, inputCls, todayISO } from '../../../components/admin/billing/shared';
import { useInvoiceAccess, ViewOnlyTag } from '../../../lib/invoiceAccess';

const STATUS_BG = {
  draft: 'bg-slate-100 text-slate-600',
  issued: 'bg-sky-100 text-sky-700',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const GstInvoicesList = () => {
  const { authHeader } = useAuth();
  const { canEdit, canDelete, viewOnly } = useInvoiceAccess();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(todayISO());

  const qDeb = useDebounced(q);
  const nextReq = useLatestRequest();
  const fetchRows = useCallback(async () => {
    setLoading(true); setErr('');
    const live = nextReq();
    try {
      const params = new URLSearchParams();
      if (qDeb.trim()) params.set('q', qDeb.trim());
      if (status) params.set('status', status);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await axios.get(`${API}/gst/invoices?${params}`, { headers: authHeader() });
      if (live()) setRows(data || []);
    } catch (e) { if (live()) setErr(formatErr(e)); }
    finally { if (live()) setLoading(false); }
  }, [authHeader, qDeb, status, dateFrom, dateTo, nextReq]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const del = async (r) => {
    if (!window.confirm(`Delete GST invoice ${r.number}? Cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/gst/invoices/${r.id}`, { headers: authHeader() });
      await fetchRows();
    } catch (e) { notifyErr(e); }
  };

  const dlPdf = async (r) => {
    try {
      const resp = await axios.get(`${API}/gst/invoices/${r.id}/pdf`, { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GST_${r.number.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) { notifyErr(e); }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="gst-invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ReceiptText className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">GST Invoices</h1>
          {viewOnly && <ViewOnlyTag />}
        </div>
        {canEdit && (
          <button onClick={() => navigate('/admin/gst/invoices/new')} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="new-gst-invoice-btn">
            <Plus size={14} /> New GST Invoice
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-8`} placeholder="Invoice no, customer, GSTIN" data-testid="gst-search" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="gst-status-filter">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No GST invoices found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Number</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Date</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Customer</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">GSTIN</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-center">POS</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Taxable</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Tax</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Total</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Status</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const t = r.totals || {};
                  const cust = r.customer_snapshot || {};
                  return (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`gst-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link to={`/admin/gst/invoices/${r.id}`} className="text-teal-700 hover:underline">{r.number}</Link>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.invoice_date}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800" data-testid={`gst-cust-${r.id}`}>
                          {[cust.prefix, cust.name].filter(Boolean).join(' ') || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{cust.gstin || <span className="text-slate-300">B2C</span>}</td>
                      <td className="px-3 py-2 text-center text-xs">{r.place_of_supply}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtINR(t.taxable_value)}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtINR(t.total_tax)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-teal-700">{fmtINR(t.grand_total)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${STATUS_BG[r.status] || 'bg-slate-100 text-slate-600'}`}>{r.status}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => navigate(`/admin/gst/invoices/${r.id}`)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title={canEdit ? 'Edit' : 'Open'} data-testid={`gst-open-${r.id}`}>{canEdit ? <Pencil size={14} /> : <Eye size={14} />}</button>
                          <button onClick={() => dlPdf(r)} className="p-1.5 rounded hover:bg-teal-50 text-teal-600" title="Download PDF" data-testid={`gst-pdf-${r.id}`}><FileDown size={14} /></button>
                          {canDelete && (
                            <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete" data-testid={`gst-delete-${r.id}`}><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GstInvoicesList;
